/**
 * DailyInsightsDashboard - Visual dashboard showing the same 4 metrics as daily email reports
 * 
 * Metrics:
 * 1. New Tier Upgrades - Customers who earned/upgraded to new tiers
 * 2. Cashier Discounts - POS discounts applied (who, why, amount)
 * 3. Revenue by Tier - Breakdown by loyalty tier
 * 4. At-Risk Customers - Inactive 30+ days sorted by lifetime value
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Crown, TrendingUp, Users, AlertTriangle, DollarSign, Percent,
  RefreshCw, Calendar, ChevronRight, Mail, Eye, Clock, Award,
  ArrowUp, ArrowDown, Loader2, CheckCircle2, UserMinus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

// Tier colors for badges
const TIER_COLORS = {
  platinum: { bg: 'bg-gradient-to-r from-gray-300 to-gray-100', text: 'text-gray-800', border: 'border-gray-400' },
  gold: { bg: 'bg-gradient-to-r from-yellow-400 to-yellow-200', text: 'text-yellow-900', border: 'border-yellow-500' },
  silver: { bg: 'bg-gradient-to-r from-gray-400 to-gray-200', text: 'text-gray-800', border: 'border-gray-500' },
  bronze: { bg: 'bg-gradient-to-r from-amber-600 to-amber-400', text: 'text-amber-100', border: 'border-amber-700' },
  non_member: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
  guest: { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200' }
};

// Tier display names
const TIER_NAMES = {
  platinum: 'Platinum',
  gold: 'Gold',
  silver: 'Silver',
  bronze: 'Bronze',
  non_member: 'Non-Member',
  guest: 'Guest'
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <div className={`rounded-xl p-5 ${color} shadow-sm border`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium opacity-80">{title}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
        {subtitle && <p className="text-sm opacity-70 mt-1">{subtitle}</p>}
      </div>
      <div className="p-2 rounded-lg bg-white/20">
        <Icon className="w-6 h-6" />
      </div>
    </div>
    {trend !== undefined && (
      <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
        {trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
        <span>{Math.abs(trend)}% vs yesterday</span>
      </div>
    )}
  </div>
);

// Tier Badge Component
const TierBadge = ({ tier }) => {
  const tierKey = tier?.toLowerCase() || 'non_member';
  const colors = TIER_COLORS[tierKey] || TIER_COLORS.non_member;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border}`}>
      {TIER_NAMES[tierKey] || tier}
    </span>
  );
};

export default function DailyInsightsDashboard({ authHeaders }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [daysBack, setDaysBack] = useState(1);
  const [lastSent, setLastSent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/daily-report/preview?days=${daysBack}`);
      setData(res.data);
    } catch (err) {
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  }, [daysBack]);

  const sendReport = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/daily-report/send');
      toast.success('Daily report email sent successfully!');
      setLastSent(new Date().toLocaleTimeString());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sending report');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading daily insights...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No data available</p>
        <Button onClick={fetchData} variant="outline" className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const { summary, tier_upgrades, cashier_discounts, revenue_by_tier, at_risk_customers, report_date } = data;
  const byTier = revenue_by_tier?.by_tier || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Daily Insights Dashboard</h2>
          <p className="text-muted-foreground">
            {report_date} • Last {daysBack} day{daysBack > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time Range Selector */}
          <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
            {[1, 7, 30].map(days => (
              <button
                key={days}
                onClick={() => setDaysBack(days)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  daysBack === days 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {days === 1 ? 'Today' : days === 7 ? '7 Days' : '30 Days'}
              </button>
            ))}
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button 
            onClick={sendReport} 
            disabled={sending}
            className="bg-primary hover:bg-primary/90"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            Send Report Email
          </Button>
        </div>
      </div>

      {lastSent && (
        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-lg">
          <CheckCircle2 className="w-4 h-4" />
          Report email sent at {lastSent}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Tier Upgrades"
          value={summary?.tier_upgrades_count || 0}
          subtitle="Customers upgraded"
          icon={Crown}
          color="bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-200"
        />
        <StatCard
          title="Cashier Discounts"
          value={summary?.cashier_discounts_count || 0}
          subtitle="Manual discounts applied"
          icon={Percent}
          color="bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-900/30 dark:to-amber-800/20 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200"
        />
        <StatCard
          title="Total Revenue"
          value={`$${(summary?.total_revenue || 0).toLocaleString()}`}
          subtitle={`${summary?.total_orders || 0} orders`}
          icon={DollarSign}
          color="bg-gradient-to-br from-green-100 to-green-50 dark:from-green-900/30 dark:to-green-800/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200"
        />
        <StatCard
          title="At-Risk Customers"
          value={summary?.at_risk_count || 0}
          subtitle="Inactive 30+ days"
          icon={UserMinus}
          color="bg-gradient-to-br from-red-100 to-red-50 dark:from-red-900/30 dark:to-red-800/20 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200"
        />
      </div>

      {/* Revenue by Tier */}
      <div className="bg-card rounded-xl border shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Revenue by Loyalty Tier
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {['platinum', 'gold', 'silver', 'bronze', 'non_member', 'guest'].map(tier => {
            const tierData = byTier[tier] || { orders: 0, revenue: 0 };
            const colors = TIER_COLORS[tier];
            return (
              <div key={tier} className={`rounded-lg p-4 ${colors.bg} ${colors.text} border ${colors.border}`}>
                <p className="text-xs font-medium opacity-80 uppercase">{TIER_NAMES[tier]}</p>
                <p className="text-xl font-bold mt-1">${tierData.revenue.toLocaleString()}</p>
                <p className="text-xs opacity-70">{tierData.orders} orders</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Total</span>
          <span className="text-xl font-bold text-foreground">
            ${(revenue_by_tier?.total_revenue || 0).toLocaleString()} from {revenue_by_tier?.total_orders || 0} orders
          </span>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tier Upgrades */}
        <div className="bg-card rounded-xl border shadow-sm p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-purple-600" />
            New Tier Upgrades
            <span className="ml-auto bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full text-sm">
              {tier_upgrades?.length || 0}
            </span>
          </h3>
          {tier_upgrades?.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {tier_upgrades.slice(0, 10).map((upgrade, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium text-foreground">{upgrade.name}</p>
                    <p className="text-sm text-muted-foreground">{upgrade.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <TierBadge tier={upgrade.tier} />
                    {upgrade.is_manual && (
                      <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded">
                        Override
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {tier_upgrades.length > 10 && (
                <p className="text-sm text-muted-foreground text-center pt-2">
                  +{tier_upgrades.length - 10} more upgrades
                </p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No tier upgrades in this period</p>
          )}
        </div>

        {/* Cashier Discounts */}
        <div className="bg-card rounded-xl border shadow-sm p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Percent className="w-5 h-5 text-amber-600" />
            Cashier Discounts Applied
            <span className="ml-auto bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full text-sm">
              {cashier_discounts?.length || 0}
            </span>
          </h3>
          {cashier_discounts?.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {cashier_discounts.slice(0, 10).map((discount, idx) => (
                <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">
                        Order #{discount.order_number} - {discount.customer_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {discount.reason || 'No reason specified'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-600 dark:text-amber-400">
                        -{discount.discount_percent}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        by {discount.applied_by}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {cashier_discounts.length > 10 && (
                <p className="text-sm text-muted-foreground text-center pt-2">
                  +{cashier_discounts.length - 10} more discounts
                </p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No cashier discounts in this period</p>
          )}
        </div>
      </div>

      {/* At-Risk Customers */}
      <div className="bg-card rounded-xl border shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          At-Risk Customers
          <span className="text-sm font-normal text-muted-foreground ml-2">
            (Inactive 30+ days, sorted by lifetime value)
          </span>
          <span className="ml-auto bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full text-sm">
            {at_risk_customers?.length || 0}
          </span>
        </h3>
        {at_risk_customers?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-3 px-2">Customer</th>
                  <th className="text-left py-3 px-2">Contact</th>
                  <th className="text-center py-3 px-2">Orders</th>
                  <th className="text-right py-3 px-2">Lifetime Value</th>
                  <th className="text-right py-3 px-2">Last Order</th>
                  <th className="text-center py-3 px-2">Tier</th>
                </tr>
              </thead>
              <tbody>
                {at_risk_customers.slice(0, 15).map((customer, idx) => (
                  <tr key={idx} className="border-b border-muted hover:bg-muted/50">
                    <td className="py-3 px-2 font-medium text-foreground">{customer.name}</td>
                    <td className="py-3 px-2 text-muted-foreground">
                      {customer.email || customer.phone || '-'}
                    </td>
                    <td className="py-3 px-2 text-center">{customer.order_count}</td>
                    <td className="py-3 px-2 text-right font-semibold text-green-600">
                      ${customer.total_spent?.toLocaleString()}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-red-600 font-medium">{customer.days_inactive} days ago</span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <TierBadge tier={customer.tier} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {at_risk_customers.length > 15 && (
              <p className="text-sm text-muted-foreground text-center pt-4">
                +{at_risk_customers.length - 15} more at-risk customers
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
            <p className="text-green-600 font-medium">No at-risk customers!</p>
            <p className="text-muted-foreground text-sm">All customers are actively engaged</p>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="text-center text-sm text-muted-foreground">
        <p>This dashboard shows the same data as the daily email report sent to administrator@bulaccinocafe.com</p>
      </div>
    </div>
  );
}
